import type { StorefrontId } from '@mentora/data-access/analytics-events'
import posthog from 'posthog-js'
import { CONSENT_STORAGE_KEY } from './consent-storage'

/**
 * Roots whose contents never reach autocapture or session replay: payment
 * sheets, the CPF/profile form, the membership application and its uploads.
 * Mark a region with `data-sensitive` (or the `ph-no-capture` class).
 */
export const SENSITIVE_SELECTORS = ['.ph-no-capture', '[data-sensitive]']

export function initAnalytics(storefront: StorefrontId, key: string | undefined, host: string): void {
  if (!key || typeof window === 'undefined')
    return
  posthog.init(key, {
    api_host: '/ingest',
    ui_host: host.replace('.i.posthog.com', '.posthog.com'),
    // Pinned below '2026-06-25', which turns on network-body streaming in
    // replay by default — that would record checkout request payloads.
    defaults: '2026-05-30',
    // Undecided visitors send nothing and store nothing; a rejection is
    // counted only through PostHog's server-side hash (requires "Cookieless
    // server hash mode" in the project settings). Do NOT add
    // `opt_out_capturing_by_default`: with it, posthog-js counts an undecided
    // visitor as rejected and captures them cookieless before they choose
    // (`isRejected()` is true for consent -1 when that flag is set).
    cookieless_mode: 'on_reject',
    // A fixed key instead of the token-derived default, so the stored decision
    // survives a project-key reset and e2e can set it.
    consent_persistence_name: CONSENT_STORAGE_KEY,
    capture_pageview: 'history_change',
    capture_exceptions: true,
    person_profiles: 'identified_only',
    autocapture: { css_selector_ignorelist: SENSITIVE_SELECTORS },
    session_recording: { maskAllInputs: true, maskTextSelector: SENSITIVE_SELECTORS.join(', ') },
  })
  registerStorefront(storefront)
}

export function registerStorefront(storefront: StorefrontId): void {
  posthog.register({ storefront })
}

export function isAnalyticsLoaded(): boolean {
  return typeof window !== 'undefined' && posthog.__loaded
}
